/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, FormGroup, ReactiveFormsModule } from "@angular/forms";
import { Subject, takeUntil } from "rxjs";

import {
  AutoCompleteModule,
  AutoCompleteCompleteEvent,
} from "primeng/autocomplete";
import { ButtonModule } from "primeng/button";
import { CheckboxModule } from "primeng/checkbox";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { TooltipModule } from "primeng/tooltip";

import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../core/services/qpilot.service";
import { SessionService } from "../../../../core/services/session.service";

import {
  SurveyAnswerFormControls,
  OfferType,
  PredefinedAnswer,
  OFFER_TYPE_OPTIONS,
  MAX_CYCLES_OPTIONS,
  QUANTITY_OPTIONS,
  DEFAULT_OFFER_TEXT,
} from "../../interfaces/retention-workflow.interface";
import { SURVEY_VALIDATION } from "../../constants/retention-workflow.constants";
import { DynamicFieldsPopoverComponent } from "../dynamic-fields-popover/dynamic-fields-popover.component";

interface ProductSuggestion {
  Id: number;
  Title: string;
  Sku?: string;
}

interface CouponSuggestion {
  Id: number;
  Code: string;
  Amount?: number;
}

@Component({
  selector: "app-survey-answer-form",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    AutoCompleteModule,
    ButtonModule,
    CheckboxModule,
    InputTextModule,
    SelectModule,
    TooltipModule,
    DynamicFieldsPopoverComponent,
  ],
  templateUrl: "./survey-answer-form.component.html",
  styleUrl: "./survey-answer-form.component.scss",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SurveyAnswerFormComponent implements OnInit, OnDestroy {
  private _formGroup!: FormGroup<SurveyAnswerFormControls>;

  @Input()
  set formGroup(value: FormGroup<SurveyAnswerFormControls>) {
    this._formGroup = value;
    // NOTE: Don't call initializeDisplayFromFormData here - preloaded Maps
    // may not be set yet. Wait for ngOnInit when all inputs are available.
  }
  get formGroup(): FormGroup<SurveyAnswerFormControls> {
    return this._formGroup;
  }

  @Input() index = 0;

  private _siteId: number | null = null;
  @Input()
  set siteId(value: number | null) {
    this._siteId = value;
    // NOTE: Don't call initializeDisplayFromFormData here - wait for ngOnInit
  }
  get siteId(): number | null {
    return this._siteId;
  }

  @Input() predefinedAnswers: PredefinedAnswer[] = [];
  @Input() preloadedProducts: Map<
    string,
    { Id: number; Title: string; Sku?: string }
  > = new Map();
  @Input() preloadedCoupons: Map<
    string,
    { Id: number; Code: string; Amount?: number }
  > = new Map();
  private _isEditMode = false;
  @Input()
  set isEditMode(value: boolean) {
    this._isEditMode = value;
    // Disable comment checkboxes when in edit mode (like old QMC behavior)
    if (this.formGroup) {
      this.updateCommentControlsState();
    }
  }
  get isEditMode(): boolean {
    return this._isEditMode;
  }

  @Input() hasCustomerAnswer = false; // True when survey has customer responses
  @Input() canDelete = true;
  @Input() inputToken?: QPilotAccessToken; // Access token for embedded mode

  @Output() remove = new EventEmitter<void>();

  private readonly destroy$ = new Subject<void>();
  private initialized = false;

  // Autocomplete suggestions
  productSuggestions: ProductSuggestion[] = [];
  couponSuggestions: CouponSuggestion[] = [];
  selectedProduct: ProductSuggestion | null = null;
  selectedCoupon: CouponSuggestion | null = null;

  // Options
  readonly offerTypeOptions = OFFER_TYPE_OPTIONS;
  readonly maxCyclesOptions = MAX_CYCLES_OPTIONS;
  readonly quantityOptions = QUANTITY_OPTIONS;
  readonly validation = SURVEY_VALIDATION;

  // Expose OfferType enum to template
  readonly OfferType = OfferType;

  constructor(
    private readonly qpilotService: QPilotService,
    private readonly sessionService: SessionService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  /**
   * Get access token - uses inputToken if provided (embedded mode), otherwise session token
   */
  private getAccessToken(): QPilotAccessToken | null | undefined {
    return this.inputToken ?? this.sessionService.accessToken;
  }

  ngOnInit(): void {
    this.setupOfferTypeListener();
    // Also try to initialize display on init if not done yet
    if (!this.initialized && this.formGroup) {
      this.initializeDisplayFromFormData();
    }
    // Ensure comment controls are disabled in edit mode
    this.updateCommentControlsState();
  }

  /**
   * Disable/enable comment checkboxes based on edit mode.
   * In edit mode, these should be disabled like in the old QMC.
   */
  private updateCommentControlsState(): void {
    if (!this.formGroup) return;

    const enableCommentControl = this.formGroup.controls.enableComment;
    const requireCommentControl = this.formGroup.controls.requireComment;

    if (this._isEditMode) {
      enableCommentControl.disable({ emitEvent: false });
      requireCommentControl.disable({ emitEvent: false });
    } else {
      enableCommentControl.enable({ emitEvent: false });
      // Require comment should stay disabled if enable comment is false
      if (enableCommentControl.value) {
        requireCommentControl.enable({ emitEvent: false });
      } else {
        requireCommentControl.disable({ emitEvent: false });
      }
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Initialize selectedProduct/selectedCoupon from existing form data
   * This is called when editing an existing survey to populate autocomplete display
   * First checks preloaded data from parent, then falls back to fetching
   */
  private initializeDisplayFromFormData(): void {
    if (this.initialized) return;

    const offerGroup = this.formGroup?.controls.offerApplicableItem;
    if (!offerGroup) return;

    const itemId = offerGroup.controls.itemId.value;
    const itemType = offerGroup.controls.itemType.value;

    // Only proceed if we have an itemId and valid type
    if (!itemId) return;

    // Check preloaded data first (from parent component)
    if (itemType === OfferType.FreeProduct) {
      const preloadedProduct = this.preloadedProducts.get(itemId);
      if (preloadedProduct) {
        this.selectedProduct = preloadedProduct;
        this.initialized = true;
        this.cdr.markForCheck();
        return;
      }
    } else if (itemType === OfferType.Discount) {
      const preloadedCoupon = this.preloadedCoupons.get(itemId);
      if (preloadedCoupon) {
        this.selectedCoupon = preloadedCoupon;
        this.initialized = true;
        this.cdr.markForCheck();
        return;
      }
    }

    // Fallback: Don't mark as initialized until we have all requirements
    // (siteId and accessToken) to actually perform the fetch
    if (!this.siteId || !this.getAccessToken()) return;

    // Now we can mark as initialized and fetch
    this.initialized = true;

    // Fetch product/coupon details based on type
    if (itemType === OfferType.FreeProduct) {
      this.fetchProductById(itemId);
    } else if (itemType === OfferType.Discount) {
      this.fetchCouponById(itemId);
    }
  }

  /**
   * Fetch product details by ID to populate the autocomplete display
   */
  private fetchProductById(productId: string): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    // Search for products using the product ID as search query
    // This ensures we find the specific product even with pagination
    this.qpilotService
      .getProductsForSite(this.siteId, accessToken, 1, productId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products) => {
          // Find exact match by ID
          const product = products?.find(
            (p: ProductSuggestion) => p.Id.toString() === productId,
          );
          if (product) {
            this.selectedProduct = product;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          // Silent fail - user can still select a new product
        },
      });
  }

  /**
   * Fetch coupon details by code to populate the autocomplete display
   * Note: The survey API stores coupon CODE in itemId, not the coupon ID
   */
  private fetchCouponById(couponCode: string): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    // Search for coupons using the coupon code as search query
    // Note: itemId from survey API contains the coupon CODE, not the coupon ID
    this.qpilotService
      .getCouponsForSite(this.siteId, accessToken, 1, couponCode)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // Find exact match by CODE (not ID - survey stores code in itemId)
          const coupon = response?.Items?.find(
            (c: CouponSuggestion) => c.Code === couponCode,
          );
          if (coupon) {
            this.selectedCoupon = coupon;
            this.cdr.markForCheck();
          }
        },
        error: () => {
          // Silent fail - user can still select a new coupon
        },
      });
  }

  private setupOfferTypeListener(): void {
    // Listen to offer type changes to set default phrase
    this.formGroup.controls.offerApplicableItem.controls.itemType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((itemType) => {
        if (itemType === OfferType.FreeProduct) {
          const currentPhrase =
            this.formGroup.controls.offerApplicableItem.controls.phrase.value;
          if (!currentPhrase) {
            this.formGroup.controls.offerApplicableItem.controls.phrase.setValue(
              DEFAULT_OFFER_TEXT.freeProduct,
            );
          }
        } else if (itemType === OfferType.Discount) {
          const currentPhrase =
            this.formGroup.controls.offerApplicableItem.controls.phrase.value;
          if (!currentPhrase) {
            this.formGroup.controls.offerApplicableItem.controls.phrase.setValue(
              DEFAULT_OFFER_TEXT.discount,
            );
          }
        }
      });
  }

  get offerType(): OfferType | null {
    return this.formGroup.controls.offerApplicableItem.controls.itemType.value;
  }

  get showProductFields(): boolean {
    return this.offerType === OfferType.FreeProduct;
  }

  get showCouponFields(): boolean {
    return this.offerType === OfferType.Discount;
  }

  get showPhraseField(): boolean {
    return (
      this.offerType === OfferType.FreeProduct ||
      this.offerType === OfferType.Discount
    );
  }

  // Comment checkbox handlers
  onEnableCommentChange(checked: boolean): void {
    if (!checked) {
      this.formGroup.controls.requireComment.setValue(false);
    }
    // Update require comment enabled state based on enable comment
    if (!this._isEditMode) {
      if (checked) {
        this.formGroup.controls.requireComment.enable({ emitEvent: false });
      } else {
        this.formGroup.controls.requireComment.disable({ emitEvent: false });
      }
    }
  }

  onRequireCommentChange(checked: boolean): void {
    if (checked) {
      this.formGroup.controls.enableComment.setValue(true);
    }
  }

  // Product search
  // Note: PrimeNG autocomplete fires completeMethod when dropdown is clicked,
  // so we don't need a separate onDropdownClick handler
  searchProducts(event: AutoCompleteCompleteEvent): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    const query = event.query?.trim() || "";

    this.qpilotService
      .getProductsForSite(this.siteId, accessToken, 1, query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (products) => {
          this.productSuggestions = products || [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.productSuggestions = [];
          this.cdr.markForCheck();
        },
      });
  }

  onProductSelect(product: ProductSuggestion): void {
    this.selectedProduct = product;
    this.formGroup.controls.offerApplicableItem.controls.itemId.setValue(
      product.Id.toString(),
    );
  }

  // Coupon search
  // Note: PrimeNG autocomplete fires completeMethod when dropdown is clicked,
  // so we don't need a separate onDropdownClick handler
  // Response format: { Items: CouponSuggestion[] }
  searchCoupons(event: AutoCompleteCompleteEvent): void {
    const accessToken = this.getAccessToken();
    if (!this.siteId || !accessToken) return;

    const query = event.query?.trim() || "";

    this.qpilotService
      .getCouponsForSite(this.siteId, accessToken, 1, query)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          // API returns { Items: [...] } format
          this.couponSuggestions = response?.Items || [];
          this.cdr.markForCheck();
        },
        error: () => {
          this.couponSuggestions = [];
          this.cdr.markForCheck();
        },
      });
  }

  onCouponSelect(coupon: CouponSuggestion): void {
    this.selectedCoupon = coupon;
    // Store the coupon CODE (not ID) - this matches what the survey API expects
    this.formGroup.controls.offerApplicableItem.controls.itemId.setValue(
      coupon.Code,
    );
  }

  // Remove answer
  onRemove(): void {
    this.remove.emit();
  }

  // Display value helpers for edit mode (show text instead of dropdowns)
  get offerTypeDisplayName(): string {
    const itemType = this.offerType;
    if (itemType === null) return "";
    const option = this.offerTypeOptions.find((opt) => opt.value === itemType);
    return option?.displayName || "";
  }

  get maxCyclesDisplayName(): string {
    const value =
      this.formGroup.controls.offerApplicableItem.controls.maxCycles.value;
    if (value === null) return "";
    const option = this.maxCyclesOptions.find((opt) => opt.value === value);
    return option?.displayName || String(value);
  }

  get quantityDisplayName(): string {
    const value =
      this.formGroup.controls.offerApplicableItem.controls.quantity.value;
    if (value === null) return "";
    const option = this.quantityOptions.find((opt) => opt.value === value);
    return option?.displayName || String(value);
  }

  get productDisplayName(): string {
    return this.selectedProduct?.Title || "";
  }

  get couponDisplayName(): string {
    return this.selectedCoupon?.Code || "";
  }
}
